import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity,
  AlertCircle,
  Archive,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FileImage,
  Headphones,
  Inbox,
  Loader2,
  MessageCircleMore,
  MessagesSquare,
  MoreVertical,
  Paperclip,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  UserRound,
  Video,
  X,
} from "lucide-react";
import { SupportService } from "../../../services/support.service";
import { getSupportSocket } from "../../../services/supportSocket";
import { getAuthUser, hasPermission, isSuperAdminAccount } from "../../AdminAuth/components/authStore";

const STATUS_META = {
  OPEN: { label: "Open", dot: "bg-amber-400", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  IN_PROGRESS: { label: "In progress", dot: "bg-sky-500", pill: "bg-sky-50 text-sky-700 border-sky-200" },
  RESOLVED: { label: "Resolved", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  CLOSED: { label: "Closed", dot: "bg-slate-400", pill: "bg-slate-50 text-slate-600 border-slate-200" },
};

const ISSUE_LABELS = {
  ORDER_ISSUE: "Order issue",
  PAYMENT_ISSUE: "Payment issue",
  PRODUCT_COMPLAINT: "Product complaint",
  DELIVERY_ISSUE: "Delivery issue",
  REFUND_ISSUE: "Refund issue",
  OTHER: "Other",
};

const money = (value, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value || 0));
  } catch {
    return `₹${Number(value || 0).toLocaleString("en-IN")}`;
  }
};

const relativeTime = (value) => {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diff)) return "";
  const min = Math.max(0, Math.floor(diff / 60000));
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return days < 7 ? `${days}d` : new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

function Avatar({ name, large = false }) {
  const initials = String(name || "U")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  const size = large ? "h-12 w-12 text-[14px]" : "h-11 w-11 text-[12px]";
  return (
    <div className={`grid ${size} shrink-0 place-items-center rounded-[14px] border border-white bg-gradient-to-br from-[#fff0f3] to-[#ffe4e9] font-black text-[#c95468] shadow-[0_7px_18px_rgba(216,104,123,0.13)] ring-1 ring-rose-100`}>
      {initials || "U"}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.OPEN;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black ${meta.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function SupportMetricCard({ icon: Icon, label, value, tone = "rose", hint }) {
  const tones = {
    rose: "from-rose-50 to-pink-50 text-[#d85d70] ring-rose-100",
    amber: "from-amber-50 to-orange-50 text-amber-600 ring-amber-100",
    sky: "from-sky-50 to-cyan-50 text-sky-600 ring-sky-100",
    emerald: "from-emerald-50 to-teal-50 text-emerald-600 ring-emerald-100",
  };
  return (
    <div className="group flex min-h-[76px] items-center gap-3 rounded-[18px] border border-white bg-white/[0.88] px-3.5 py-3 shadow-[0_10px_28px_rgba(30,41,59,0.07)] ring-1 ring-slate-200/70 backdrop-blur transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_15px_34px_rgba(30,41,59,0.10)]">
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-[13px] bg-gradient-to-br ${tones[tone] || tones.rose} ring-1`}>
        <Icon size={18} />
      </span>
      <span className="min-w-0">
        <span className="block text-[19px] font-black leading-none tracking-[-0.03em] text-slate-950">{Number(value || 0).toLocaleString("en-IN")}</span>
        <span className="mt-1 block text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">{label}</span>
        {hint ? <span className="mt-0.5 block truncate text-[10px] font-semibold text-slate-400">{hint}</span> : null}
      </span>
    </div>
  );
}

function ConversationRow({ item, selected, onClick }) {
  const user = item.user || {};
  const issue = ISSUE_LABELS[item.issueType] || item.issueType || "Support";
  const statusMeta = STATUS_META[item.status] || STATUS_META.OPEN;
  const unread = Number(item.adminUnreadCount || 0);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative mx-2 mb-2 w-[calc(100%_-_16px)] overflow-hidden rounded-[17px] border p-3 text-left transition-all duration-200 ${
        selected
          ? "border-rose-200 bg-gradient-to-r from-[#fff2f5] to-white shadow-[0_9px_22px_rgba(216,104,123,0.11)]"
          : "border-slate-200/80 bg-white hover:-translate-y-0.5 hover:border-rose-100 hover:shadow-[0_8px_20px_rgba(15,23,42,0.07)]"
      }`}
    >
      {selected ? <span className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-[#d8687b]" /> : null}
      <div className="flex gap-3">
        <div className="relative pt-0.5">
          <Avatar name={user.name} />
          {unread > 0 ? (
            <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#df5f76] px-1 text-[9px] font-black text-white ring-2 ring-white">
              {Math.min(99, unread)}
            </span>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate text-[14px] font-black tracking-[-0.01em] text-slate-900">{user.name || "Customer"}</div>
            <div className="shrink-0 text-[11px] font-bold text-slate-400">{relativeTime(item.lastMessageAt)}</div>
          </div>
          <div className="mt-1 truncate text-[12px] font-bold text-slate-600">{item.subject || issue}</div>
          <div className={`mt-1.5 line-clamp-2 text-[11px] leading-[1.45rem] ${unread ? "font-bold text-slate-700" : "font-medium text-slate-500"}`}>
            {item.lastMessagePreview || "No messages yet"}
          </div>
          <div className="mt-2.5 flex min-w-0 items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1.5 text-[10px] font-black text-slate-600">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusMeta.dot}`} />
              {statusMeta.label}
            </span>
            <span className="max-w-[145px] truncate rounded-full bg-[#fff0f3] px-2.5 py-1.5 text-[10px] font-black text-[#d85d70]">{issue}</span>
            {Number(item.reopenCount || 0) > 0 ? <span className="rounded-full bg-violet-50 px-2.5 py-1.5 text-[10px] font-black text-violet-600">Reopened {item.reopenCount}×</span> : null}
            {item.userClearedAt ? <Archive size={12} className="ml-auto shrink-0 text-sky-500" /> : null}
          </div>
        </div>
      </div>
    </button>
  );
}

function AttachmentView({ attachment }) {
  if (!attachment?.url) return null;
  if (attachment.mediaType === "VIDEO") {
    return (
      <video controls preload="metadata" className="mt-2 max-h-[300px] w-full max-w-[420px] rounded-[18px] bg-black/90 object-contain shadow-sm">
        <source src={attachment.url} type={attachment.mimeType || "video/mp4"} />
      </video>
    );
  }
  return (
    <a href={attachment.url} target="_blank" rel="noreferrer" className="mt-2 block">
      <img src={attachment.url} alt={attachment.originalName || "Support attachment"} className="max-h-[320px] max-w-[420px] rounded-[18px] object-cover shadow-sm ring-1 ring-black/5" />
    </a>
  );
}

function MessageBubble({ message }) {
  const isAdmin = message.senderType === "ADMIN";
  const isSystem = message.senderType === "SYSTEM";
  if (isSystem) {
    return (
      <div className="my-5 flex items-center justify-center gap-2 text-[9px] font-black uppercase tracking-[0.08em] text-slate-400">
        <span className="h-px w-10 bg-slate-200" />
        <span className="rounded-full bg-white/80 px-3 py-1.5 shadow-sm ring-1 ring-slate-200/80">{message.text}</span>
        <span className="h-px w-10 bg-slate-200" />
      </div>
    );
  }
  return (
    <div className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[84%] rounded-[19px] px-3.5 py-2.5 shadow-sm sm:max-w-[72%] ${isAdmin ? "rounded-br-[6px] bg-gradient-to-br from-[#e16a7d] to-[#d35b70] text-white" : "rounded-bl-[6px] border border-slate-200 bg-white text-slate-800"}`}>
        {message.text ? <div className="whitespace-pre-wrap break-words text-[13px] font-medium leading-[1.65]">{message.text}</div> : null}
        {(message.attachments || []).map((attachment) => <AttachmentView key={attachment.id} attachment={attachment} />)}
        <div className={`mt-1.5 text-right text-[10px] font-bold ${isAdmin ? "text-white/70" : "text-slate-400"}`}>
          {new Date(message.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          {isAdmin && message.readByUserAt ? " · Read" : ""}
        </div>
      </div>
    </div>
  );
}

function EmptyConversation() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center">
      <div className="relative">
        <div className="absolute inset-0 rounded-[24px] bg-rose-200/30 blur-2xl" />
        <div className="relative grid h-20 w-20 place-items-center rounded-[24px] border border-white bg-gradient-to-br from-white to-rose-50 text-[#d8687b] shadow-[0_16px_36px_rgba(216,104,123,0.12)] ring-1 ring-rose-100">
          <Headphones size={31} />
        </div>
      </div>
      <h3 className="mt-5 text-[18px] font-black tracking-[-0.02em] text-slate-900">Open a customer conversation</h3>
      <p className="mt-2 max-w-xs text-[11px] font-medium leading-5 text-slate-400">Select a request from the inbox to review the full history, order context and reply.</p>
    </div>
  );
}

function ConfirmModal({ open, title, description, confirmText, danger = false, inputValue, onInput, requiredText, onCancel, onConfirm, loading }) {
  if (!open) return null;
  const disabled = loading || (requiredText && inputValue !== requiredText);
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/35 p-4 backdrop-blur-[3px]">
      <div className="w-full max-w-[440px] overflow-hidden rounded-[26px] border border-white/90 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.24)]">
        <div className="bg-[radial-gradient(circle_at_85%_0%,rgba(251,191,203,0.30),transparent_34%),linear-gradient(135deg,#fff,#fff7f9)] p-5">
          <div className={`grid h-12 w-12 place-items-center rounded-[16px] border ${danger ? "border-red-100 bg-red-50 text-red-600" : "border-rose-100 bg-rose-50 text-[#d85d70]"}`}>
            <AlertCircle size={22} />
          </div>
          <h3 className="mt-4 text-[18px] font-black tracking-[-0.02em] text-slate-950">{title}</h3>
          <p className="mt-2 text-[12px] font-medium leading-5 text-slate-500">{description}</p>
          {requiredText ? (
            <div className="mt-4">
              <label className="text-[10px] font-black uppercase tracking-[0.08em] text-slate-400">Confirmation</label>
              <div className="mt-1.5 text-[11px] font-semibold text-slate-500">Type <span className="font-black text-slate-800">{requiredText}</span></div>
              <input value={inputValue} onChange={(event) => onInput?.(event.target.value)} className="mt-2 h-11 w-full rounded-[13px] border border-slate-200 bg-white px-3 text-[13px] font-semibold outline-none transition focus:border-rose-300 focus:ring-4 focus:ring-rose-50" />
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-4">
          <button type="button" onClick={onCancel} className="h-10 rounded-[12px] border border-slate-200 bg-white px-4 text-[11px] font-black text-slate-600 transition hover:bg-slate-50">Cancel</button>
          <button type="button" disabled={disabled} onClick={onConfirm} className={`h-10 rounded-[12px] px-4 text-[11px] font-black text-white transition disabled:cursor-not-allowed disabled:opacity-45 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-[#d8687b] hover:bg-[#c95b69]"}`}>
            {loading ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Processing</span> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupportInbox() {
  const supportActor = getAuthUser() || {};
  const supportRole = String(supportActor?.role || "").trim().toLowerCase();
  const canClearAll =
    isSuperAdminAccount() ||
    supportRole === "admin" ||
    hasPermission("admin.support");
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({ chatEnabled: true, disabledMessage: "" });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("ALL");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [disabledMessageDraft, setDisabledMessageDraft] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [clearOneOpen, setClearOneOpen] = useState(false);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearAllText, setClearAllText] = useState("");
  const [contextOpen, setContextOpen] = useState(false);
  const [conversationsCollapsed, setConversationsCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("supportConversationsCollapsed") === "1";
    } catch {
      return false;
    }
  });
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const searchTimer = useRef(null);

  const selected = detail?.conversation || items.find((item) => item.id === selectedId) || null;

  const toggleConversationsPanel = () => {
    setConversationsCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem("supportConversationsCollapsed", next ? "1" : "0");
      } catch {
        // Storage is optional; UI state still works for the current session.
      }
      return next;
    });
  };

  const loadStats = useCallback(async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([SupportService.getStats(), SupportService.getSettings()]);
      setStats(statsRes?.data || {});
      const nextSettings = settingsRes?.data || {};
      setSettings(nextSettings);
      setDisabledMessageDraft(nextSettings.disabledMessage || "");
    } catch (error) {
      console.error("Failed to load support stats", error);
    }
  }, []);

  const loadList = useCallback(async ({ keepSelection = true } = {}) => {
    setLoadingList(true);
    try {
      const res = await SupportService.listConversations({ page, limit: 40, status, q, unreadOnly: unreadOnly ? 1 : "" });
      const data = res?.data || {};
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
      if (!keepSelection && data.items?.length) setSelectedId(data.items[0].id);
      if (selectedId && !data.items?.some((item) => item.id === selectedId) && data.items?.length) setSelectedId(data.items[0].id);
    } catch (error) {
      console.error("Failed to load support conversations", error);
    } finally {
      setLoadingList(false);
    }
  }, [page, status, q, unreadOnly, selectedId]);

  const loadConversation = useCallback(async (id, { scroll = true } = {}) => {
    if (!id) return;
    setMessagesLoading(true);
    try {
      const [detailRes, messagesRes] = await Promise.all([
        SupportService.getConversation(id),
        SupportService.getMessages(id, { limit: 80 }),
        SupportService.markRead(id).catch(() => null),
      ]);
      setDetail(detailRes?.data || null);
      const messageData = messagesRes?.data || {};
      setMessages(messageData.items || []);
      setHasMoreMessages(Boolean(messageData.hasMore));
      setNextBefore(messageData.nextBefore || null);
      setItems((current) => current.map((item) => item.id === id ? { ...item, adminUnreadCount: 0 } : item));
      loadStats();
      if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }), 20);
    } catch (error) {
      console.error("Failed to load support conversation", error);
      setDetail(null);
    } finally {
      setMessagesLoading(false);
    }
  }, [loadStats]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [page, status, q, unreadOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedId) loadConversation(selectedId); else setDetail(null); }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setContextOpen(false); }, [selectedId]);

  useEffect(() => {
    const socket = getSupportSocket();
    if (!socket) return undefined;
    const refresh = () => {
      loadStats();
      loadList();
    };
    const onMessage = (payload) => {
      refresh();
      if (payload?.conversation?.id === selectedId) {
        setMessages((current) => current.some((item) => item.id === payload.message?.id) ? current : [...current, payload.message]);
        SupportService.markRead(selectedId).catch(() => null);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
      }
    };
    const onConversation = (conversation) => {
      setItems((current) => current.map((item) => item.id === conversation?.id ? { ...item, ...conversation } : item));
      if (conversation?.id === selectedId) setDetail((current) => current ? { ...current, conversation: { ...current.conversation, ...conversation } } : current);
      loadStats();
    };
    const onCleared = ({ conversationId, conversation }) => {
      setItems((current) => current.map((item) => item.id === conversationId && conversation ? { ...item, ...conversation } : item));
      if (selectedId === conversationId) loadConversation(conversationId, { scroll: false });
      loadStats();
      loadList();
    };
    const onAllCleared = () => {
      loadStats();
      loadList();
      if (selectedId) loadConversation(selectedId, { scroll: false });
    };
    socket.on("support:message-created", onMessage);
    socket.on("support:conversation-updated", onConversation);
    socket.on("support:conversation-cleared", onCleared);
    socket.on("support:all-cleared", onAllCleared);
    socket.on("support:settings-updated", (value) => { setSettings(value); setDisabledMessageDraft(value.disabledMessage || ""); });
    return () => {
      socket.off("support:message-created", onMessage);
      socket.off("support:conversation-updated", onConversation);
      socket.off("support:conversation-cleared", onCleared);
      socket.off("support:all-cleared", onAllCleared);
      socket.off("support:settings-updated");
    };
  }, [selectedId, loadList, loadStats]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); setQ(search.trim()); }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const loadEarlier = async () => {
    if (!selectedId || !nextBefore) return;
    try {
      const res = await SupportService.getMessages(selectedId, { limit: 80, before: nextBefore });
      const data = res?.data || {};
      setMessages((current) => [...(data.items || []), ...current]);
      setHasMoreMessages(Boolean(data.hasMore));
      setNextBefore(data.nextBefore || null);
    } catch (error) {
      console.error(error);
    }
  };

  const sendReply = async () => {
    if (!selectedId || sending || (!reply.trim() && !files.length)) return;
    setSending(true);
    try {
      const res = await SupportService.sendMessage(selectedId, { message: reply.trim(), files });
      const payload = res?.data || {};
      if (payload.message) setMessages((current) => current.some((item) => item.id === payload.message.id) ? current : [...current, payload.message]);
      setReply("");
      setFiles([]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
      loadStats();
      loadList();
    } catch (error) {
      window.alert(error?.message || "Could not send support reply");
    } finally {
      setSending(false);
    }
  };

  const chooseFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;
    const videos = picked.filter((file) => file.type.startsWith("video/"));
    const images = picked.filter((file) => file.type.startsWith("image/"));
    if (videos.length > 1 || (videos.length && images.length)) {
      window.alert("Send either up to 4 images or one video in a single message.");
      event.target.value = "";
      return;
    }
    setFiles(picked.slice(0, 4));
    event.target.value = "";
  };

  const changeStatus = async (nextStatus) => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const res = await SupportService.updateStatus(selectedId, nextStatus);
      const conversation = res?.data;
      setDetail((current) => current ? { ...current, conversation } : current);
      setItems((current) => current.map((item) => item.id === selectedId ? { ...item, ...conversation } : item));
      loadStats();
    } catch (error) {
      window.alert(error?.message || "Could not update status");
    } finally {
      setActionLoading(false);
    }
  };

  const toggleChat = async () => {
    try {
      const res = await SupportService.updateSettings({ chatEnabled: !settings.chatEnabled });
      setSettings(res?.data || settings);
    } catch (error) {
      window.alert(error?.message || "Could not update support availability");
    }
  };

  const saveSettings = async () => {
    try {
      setActionLoading(true);
      const res = await SupportService.updateSettings({ disabledMessage: disabledMessageDraft.trim() });
      setSettings(res?.data || settings);
      setSettingsOpen(false);
    } catch (error) {
      window.alert(error?.message || "Could not save support settings");
    } finally {
      setActionLoading(false);
    }
  };

  const doClearOne = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await SupportService.clearConversation(selectedId);
      setClearOneOpen(false);
      setPage(1);
      setUnreadOnly(false);
      setStatus("CLEARED");
      await Promise.all([loadConversation(selectedId, { scroll: false }), loadStats()]);
    } catch (error) {
      window.alert(error?.message || "Could not clear conversation from the customer");
    } finally {
      setActionLoading(false);
    }
  };

  const doClearAll = async () => {
    setActionLoading(true);
    try {
      await SupportService.clearAll(clearAllText);
      setClearAllOpen(false);
      setClearAllText("");
      setSelectedId(null);
      setDetail(null);
      setMessages([]);
      setPage(1);
      setUnreadOnly(false);
      setStatus("CLEARED");
      await loadStats();
    } catch (error) {
      window.alert(error?.message || "Could not clear chats from customers");
    } finally {
      setActionLoading(false);
    }
  };

  const filters = [
    ["ALL", "Active"],
    ["OPEN", "Open"],
    ["IN_PROGRESS", "In progress"],
    ["RESOLVED", "Resolved"],
    ["CLEARED", "Cleared history"],
  ];

  return (
    <div className="flex min-h-0 flex-col gap-4 xl:h-[calc(100vh-104px)] xl:overflow-hidden">
      <section className="relative shrink-0 overflow-hidden rounded-[26px] border border-white/90 bg-[radial-gradient(circle_at_88%_0%,rgba(255,183,200,0.24),transparent_28%),radial-gradient(circle_at_10%_0%,rgba(208,213,255,0.16),transparent_30%),linear-gradient(120deg,#ffffff_0%,#fffafb_52%,#fff4f7_100%)] px-5 py-4 shadow-[0_18px_45px_rgba(34,45,82,0.08)] ring-1 ring-slate-200/70 sm:px-6 xl:py-4">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-gradient-to-br from-orange-200/20 via-rose-200/35 to-violet-200/20 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-[32%] h-48 w-48 rounded-full bg-rose-100/35 blur-3xl" />

        <div className="relative z-10 grid gap-4 xl:grid-cols-[minmax(310px,0.8fr)_minmax(560px,1.2fr)] xl:items-center">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-rose-200/80 bg-white/80 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.14em] text-[#df5f76] shadow-sm backdrop-blur">
                <Headphones size={12} /> Customer care workspace
              </span>
              <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[9px] font-black ${settings.chatEnabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                <span className="relative flex h-2 w-2">
                  {settings.chatEnabled ? <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-35 motion-reduce:animate-none" /> : null}
                  <span className={`relative inline-flex h-2 w-2 rounded-full ${settings.chatEnabled ? "bg-emerald-500" : "bg-slate-400"}`} />
                </span>
                {settings.chatEnabled ? "Live" : "Paused"}
              </span>
            </div>

            <h1 className="mt-3 text-[clamp(28px,3vw,40px)] font-black leading-none tracking-[-0.045em] text-[#101828]">
              Support <span className="bg-gradient-to-r from-[#d85d70] via-[#e77889] to-[#ca647a] bg-clip-text text-transparent">Center</span>
            </h1>
            <p className="mt-2 max-w-xl text-[11px] font-semibold leading-5 text-slate-500">Handle customer issues, linked orders and live conversations from one place.</p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={actionLoading}
                onClick={toggleChat}
                className={`group inline-flex h-10 items-center gap-2 rounded-[13px] border px-3.5 text-[10px] font-black shadow-sm transition-all duration-300 hover:-translate-y-0.5 ${settings.chatEnabled ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}
              >
                <span className={`relative h-5 w-9 rounded-full transition ${settings.chatEnabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${settings.chatEnabled ? "left-[18px]" : "left-0.5"}`} />
                </span>
                {settings.chatEnabled ? "Accepting chats" : "Chat paused"}
              </button>
              <button
                type="button"
                onClick={() => setSettingsOpen((value) => !value)}
                className={`inline-flex h-10 items-center gap-2 rounded-[13px] border px-3.5 text-[10px] font-black shadow-sm transition-all hover:-translate-y-0.5 ${settingsOpen ? "border-rose-200 bg-rose-50 text-[#d8687b]" : "border-white bg-white/90 text-slate-600 ring-1 ring-slate-200/80 hover:text-[#d8687b]"}`}
              >
                <Settings2 size={14} /> Settings
              </button>
              <button
                type="button"
                onClick={() => { loadStats(); loadList(); if (selectedId) loadConversation(selectedId, { scroll: false }); }}
                className="inline-flex h-10 items-center gap-2 rounded-[13px] border border-white bg-white/90 px-3.5 text-[10px] font-black text-slate-600 shadow-sm ring-1 ring-slate-200/80 transition-all hover:-translate-y-0.5 hover:text-[#d8687b]"
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 xl:grid-cols-2 2xl:grid-cols-4">
            <SupportMetricCard icon={Inbox} label="Open" value={stats.open} tone="amber" hint="Waiting for action" />
            <SupportMetricCard icon={Activity} label="In progress" value={stats.inProgress} tone="sky" hint="Being handled" />
            <SupportMetricCard icon={MessagesSquare} label="Unread" value={stats.unreadConversations} tone="rose" hint={`${Number(stats.unreadMessages || 0)} messages`} />
            <SupportMetricCard icon={CheckCircle2} label="Resolved" value={stats.resolved} tone="emerald" hint="Completed requests" />
          </div>
        </div>
      </section>

      {settingsOpen ? (
        <section className="shrink-0 overflow-hidden rounded-[20px] border border-white bg-white/95 shadow-[0_12px_30px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/70">
          <div className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-end sm:px-5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400"><Settings2 size={14} className="text-[#d8687b]" /> Support availability</div>
              <label className="mt-2 block text-[10px] font-black text-slate-700">Message shown when chat is paused</label>
              <input value={disabledMessageDraft} onChange={(event) => setDisabledMessageDraft(event.target.value)} placeholder="Support chat is currently unavailable." className="mt-1.5 h-10 w-full rounded-[12px] border border-slate-200 bg-slate-50 px-3 text-[11px] font-semibold text-slate-700 outline-none transition focus:border-rose-300 focus:bg-white focus:ring-4 focus:ring-rose-50" />
            </div>
            <button type="button" disabled={actionLoading} onClick={saveSettings} className="h-10 rounded-[12px] bg-[#d8687b] px-4 text-[10px] font-black text-white shadow-[0_8px_18px_rgba(216,104,123,0.22)] transition hover:bg-[#c95b69] disabled:opacity-50">Save changes</button>
            {canClearAll ? <button type="button" onClick={() => setClearAllOpen(true)} className="h-10 rounded-[12px] border border-red-100 bg-red-50 px-4 text-[10px] font-black text-red-600 transition hover:bg-red-100">Clear all from customers</button> : null}
          </div>
        </section>
      ) : null}

      <section className="min-h-0 flex-1 overflow-hidden rounded-[26px] border border-white bg-white shadow-[0_20px_48px_rgba(15,23,42,0.09)] ring-1 ring-slate-200/75">
        <div className={`grid h-full min-h-0 ${conversationsCollapsed ? "xl:grid-cols-[minmax(0,1fr)] 2xl:grid-cols-[minmax(0,1fr)_320px]" : "xl:grid-cols-[320px_minmax(0,1fr)] 2xl:grid-cols-[340px_minmax(0,1fr)_320px]"}`}>
          {!conversationsCollapsed ? (
            <aside className="flex min-h-0 flex-col border-b border-slate-200/80 bg-[#fbfbfc] xl:border-b-0 xl:border-r">
              <div className="border-b border-slate-200/70 bg-white px-4 pb-3.5 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-[17px] font-black tracking-[-0.02em] text-slate-950">Conversations</div>
                      <span className="rounded-full bg-[#fff0f3] px-2.5 py-1 text-[10px] font-black text-[#d85d70]">{total.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mt-1 text-[11px] font-semibold text-slate-500">{Number(stats.unreadMessages || 0)} unread messages</div>
                  </div>
                  <button type="button" onClick={toggleConversationsPanel} className="grid h-9 w-9 place-items-center rounded-[11px] border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d8687b]" title="Hide conversations" aria-label="Hide conversations"><PanelLeftClose size={15} /></button>
                </div>

                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search customer, order, subject" className="h-11 w-full rounded-[13px] border border-slate-200 bg-slate-50 pl-9 pr-3 text-[12px] font-semibold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-rose-300 focus:bg-white focus:ring-4 focus:ring-rose-50" />
                </div>

                <div className="mt-3 flex gap-1.5 overflow-x-auto pb-0.5">
                  {filters.map(([value, label]) => (
                    <button key={value} type="button" onClick={() => { setPage(1); setStatus(value); if (value === "CLEARED") setUnreadOnly(false); }} className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-black transition ${status === value ? "bg-[#d8687b] text-white shadow-[0_5px_12px_rgba(216,104,123,0.2)]" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{label}</button>
                  ))}
                  <button type="button" onClick={() => { setPage(1); if (status === "CLEARED") setStatus("ALL"); setUnreadOnly((value) => !value); }} className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-black transition ${unreadOnly ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Unread</button>
                </div>
              </div>

              <div className="min-h-[330px] flex-1 overflow-y-auto py-2">
                {loadingList ? (
                  <div className="grid h-48 place-items-center text-slate-400"><Loader2 className="animate-spin" size={22} /></div>
                ) : items.length ? items.map((item) => (
                  <ConversationRow key={item.id} item={item} selected={item.id === selectedId} onClick={() => setSelectedId(item.id)} />
                )) : (
                  <div className="px-7 py-20 text-center">
                    <div className="mx-auto grid h-14 w-14 place-items-center rounded-[18px] bg-white text-slate-300 shadow-sm ring-1 ring-slate-200"><Inbox size={23} /></div>
                    <div className="mt-4 text-[12px] font-black text-slate-700">No conversations found</div>
                    <div className="mt-1 text-[11px] font-semibold text-slate-400">Try another filter or search.</div>
                  </div>
                )}
              </div>

              {total > 40 ? (
                <div className="flex items-center justify-between border-t border-slate-200/70 bg-white p-3">
                  <button disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))} className="rounded-[10px] border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-black text-slate-600 disabled:opacity-35">Previous</button>
                  <span className="text-[10px] font-bold text-slate-400">Page {page}</span>
                  <button disabled={page * 40 >= total} onClick={() => setPage((value) => value + 1)} className="rounded-[10px] border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-black text-slate-600 disabled:opacity-35">Next</button>
                </div>
              ) : null}
            </aside>
          ) : null}

          <main className="relative flex min-h-0 min-w-0 flex-col bg-[radial-gradient(circle_at_50%_0%,rgba(255,241,244,0.8),transparent_32%),#f8f9fb]">
            {conversationsCollapsed ? (
              <button type="button" onClick={toggleConversationsPanel} className="absolute left-3 top-3 z-40 inline-flex h-9 items-center gap-2 rounded-[11px] border border-slate-200 bg-white/95 px-2.5 text-[9px] font-black text-slate-600 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d8687b]" title="Show conversations" aria-label="Show conversations">
                <PanelLeftOpen size={15} /><span className="hidden sm:inline">Conversations</span>
              </button>
            ) : null}

            {!selected ? <EmptyConversation /> : (
              <>
                <header className={`flex min-h-[72px] items-center justify-between gap-3 border-b border-slate-200/80 bg-white/95 py-3 backdrop-blur ${conversationsCollapsed ? "pl-36 pr-4 sm:pl-40" : "px-4"}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={detail?.user?.name || selected?.user?.name} large />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="truncate text-[15px] font-black tracking-[-0.01em] text-slate-950">{detail?.user?.name || selected?.user?.name || "Customer"}</div>
                        <StatusPill status={selected.status} />
                        {Number(selected.reopenCount || 0) > 0 ? <span className="rounded-full bg-violet-50 px-2 py-1 text-[9px] font-black text-violet-600">Reopened {selected.reopenCount}×</span> : null}
                        {selected.userClearedAt ? <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-1 text-[9px] font-black text-sky-600"><Archive size={11} /> Cleared</span> : null}
                      </div>
                      <div className="mt-1 flex min-w-0 items-center gap-2 text-[11px] font-semibold text-slate-400">
                        <span className="max-w-[360px] truncate text-slate-600">{selected.subject}</span>
                        <span>•</span>
                        <span className="shrink-0">{ISSUE_LABELS[selected.issueType] || selected.issueType}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button type="button" onClick={() => setContextOpen(true)} className="hidden h-10 items-center gap-2 rounded-[12px] border border-slate-200 bg-white px-3 text-[11px] font-black text-slate-600 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d8687b] xl:inline-flex 2xl:hidden" title="Customer details">
                      <UserRound size={15} /> Details
                    </button>
                    {!selected.userClearedAt ? (
                      <div className="relative hidden sm:block">
                        <select value={selected.status || "OPEN"} disabled={actionLoading} onChange={(event) => changeStatus(event.target.value)} className="h-10 appearance-none rounded-[12px] border border-slate-200 bg-white pl-3 pr-8 text-[11px] font-black text-slate-700 shadow-sm outline-none transition focus:border-rose-300 focus:ring-4 focus:ring-rose-50">
                          <option value="OPEN">Open</option>
                          <option value="IN_PROGRESS">In progress</option>
                          <option value="RESOLVED">Resolved</option>
                          <option value="CLOSED">Closed</option>
                        </select>
                        <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                    ) : null}

                    <div className="relative">
                      <button type="button" onClick={() => setMenuOpen((value) => !value)} className="grid h-10 w-10 place-items-center rounded-[12px] border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d8687b]"><MoreVertical size={17} /></button>
                      {menuOpen ? (
                        <div className="absolute right-0 top-12 z-30 w-52 rounded-[16px] border border-slate-200 bg-white p-1.5 shadow-[0_18px_46px_rgba(15,23,42,0.16)]">
                          {!selected.userClearedAt ? (
                            <>
                              <button type="button" onClick={async () => { setMenuOpen(false); try { const res = await SupportService.assignToMe(selectedId); setDetail((current) => current ? { ...current, conversation: res.data } : current); } catch (error) { window.alert(error.message); } }} className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2.5 text-left text-[10px] font-bold text-slate-700 transition hover:bg-slate-50"><ShieldCheck size={14} className="text-[#d8687b]" /> Assign to me</button>
                              <button type="button" onClick={() => { setMenuOpen(false); setClearOneOpen(true); }} className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2.5 text-left text-[10px] font-bold text-sky-700 transition hover:bg-sky-50"><Archive size={14} /> Clear from customer</button>
                            </>
                          ) : (
                            <div className="rounded-[10px] bg-slate-50 px-3 py-2.5 text-[10px] font-bold text-slate-400">Read-only history</div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </header>

                <div className="flex-1 overflow-y-auto px-3 py-5 sm:px-5">
                  {messagesLoading ? (
                    <div className="grid h-64 place-items-center text-slate-400"><Loader2 className="animate-spin" size={24} /></div>
                  ) : (
                    <div className="mx-auto max-w-4xl space-y-3">
                      {hasMoreMessages ? <div className="text-center"><button type="button" onClick={loadEarlier} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-[9px] font-black text-slate-500 shadow-sm transition hover:bg-slate-50">Load earlier messages</button></div> : null}
                      {messages.map((message) => <MessageBubble key={message.id} message={message} />)}
                      <div ref={bottomRef} />
                    </div>
                  )}
                </div>

                <footer className="border-t border-slate-200/80 bg-white/95 p-3 backdrop-blur sm:p-4">
                  {files.length ? (
                    <div className="mb-2.5 flex flex-wrap gap-2">
                      {files.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="flex items-center gap-2 rounded-[11px] border border-rose-100 bg-rose-50 px-2.5 py-1.5 text-[9px] font-bold text-[#d65a6d]">
                          {file.type.startsWith("video/") ? <Video size={12} /> : <FileImage size={12} />}
                          <span className="max-w-[160px] truncate">{file.name}</span>
                          <button type="button" onClick={() => setFiles((current) => current.filter((_, idx) => idx !== index))}><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {selected.userClearedAt ? (
                    <div className="flex items-center justify-center gap-2 rounded-[15px] border border-sky-100 bg-sky-50 px-4 py-3 text-[11px] font-black text-sky-700"><Archive size={14} /> Cleared from customer · Admin history retained</div>
                  ) : selected.status === "CLOSED" ? (
                    <div className="flex items-center justify-center gap-2 rounded-[15px] border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-500"><CheckCircle2 size={14} /> Closed · Customer can reopen this same request if the issue continues</div>
                  ) : (
                    <div className="flex items-end gap-2 rounded-[17px] border border-slate-200 bg-slate-50/80 p-2 shadow-inner">
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" multiple className="hidden" onChange={chooseFiles} />
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] border border-white bg-white text-slate-500 shadow-sm transition hover:text-[#d65a6d]" title="Attach media"><Paperclip size={16} /></button>
                      <textarea value={reply} onChange={(event) => setReply(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendReply(); } }} rows={1} placeholder="Write a reply..." className="min-h-[40px] max-h-32 flex-1 resize-none border-0 bg-transparent px-2 py-2.5 text-[13px] font-medium text-slate-700 outline-none placeholder:text-slate-400" />
                      <button type="button" disabled={sending || (!reply.trim() && !files.length)} onClick={sendReply} className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-[#d8687b] text-white shadow-[0_8px_18px_rgba(216,104,123,0.24)] transition hover:bg-[#c95b69] disabled:cursor-not-allowed disabled:opacity-40">{sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}</button>
                    </div>
                  )}
                </footer>
              </>
            )}
          </main>

          {contextOpen && selected ? (
            <button type="button" aria-label="Close customer details" onClick={() => setContextOpen(false)} className="fixed inset-0 z-[60] hidden bg-slate-950/15 backdrop-blur-[1px] xl:block 2xl:hidden" />
          ) : null}

          <aside className={`${contextOpen ? "xl:block" : "xl:hidden"} fixed bottom-4 right-4 top-[92px] z-[70] hidden min-h-0 w-[360px] overflow-hidden rounded-[24px] border border-white bg-[#fbfbfc] shadow-[0_24px_70px_rgba(15,23,42,0.22)] ring-1 ring-slate-200/80 2xl:static 2xl:block 2xl:w-auto 2xl:rounded-none 2xl:border-y-0 2xl:border-r-0 2xl:border-l 2xl:border-slate-200/80 2xl:shadow-none 2xl:ring-0`}>
            {!selected ? null : (
              <div className="h-full overflow-y-auto p-4">
                <div className="mb-3 flex items-center justify-between px-1">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">Customer context</div>
                    <div className="mt-1 text-[17px] font-black tracking-[-0.02em] text-slate-950">Request details</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-[11px] bg-rose-50 text-[#d8687b]"><UserRound size={17} /></div>
                    <button type="button" onClick={() => setContextOpen(false)} className="grid h-9 w-9 place-items-center rounded-[11px] border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-[#d8687b] 2xl:hidden" title="Close details"><X size={16} /></button>
                  </div>
                </div>

                <div className="rounded-[20px] border border-white bg-white p-4 shadow-[0_9px_22px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/70">
                  <div className="flex items-center gap-3">
                    <Avatar name={detail?.user?.name} large />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-black text-slate-950">{detail?.user?.name || "Customer"}</div>
                      <div className="mt-1 truncate text-[11px] font-semibold text-slate-500">{detail?.user?.phone || "No phone"}</div>
                    </div>
                  </div>
                  {detail?.user?.email ? <div className="mt-3 truncate rounded-[11px] bg-slate-50 px-3 py-2.5 text-[11px] font-semibold text-slate-600">{detail.user.email}</div> : null}
                  {detail?.user?.userCode ? <div className="mt-2 inline-flex rounded-full bg-[#fff0f3] px-2.5 py-1.5 text-[10px] font-black text-[#d8687b]">{detail.user.userCode}</div> : null}
                </div>

                <div className="mt-3 rounded-[20px] border border-white bg-white p-4 shadow-[0_9px_22px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/70">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-[10px] bg-violet-50 text-violet-600"><MessageCircleMore size={14} /></span><span className="text-[12px] font-black text-slate-800">Request</span></div>
                    <StatusPill status={selected.status} />
                  </div>
                  <dl className="mt-3 space-y-3 text-[11px]">
                    <div className="flex items-start justify-between gap-3"><dt className="font-semibold text-slate-400">Issue</dt><dd className="text-right font-black text-slate-700">{ISSUE_LABELS[selected.issueType] || selected.issueType}</dd></div>
                    <div className="flex items-start justify-between gap-3"><dt className="font-semibold text-slate-400">Created</dt><dd className="text-right font-bold text-slate-700">{new Date(selected.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</dd></div>
                    <div className="flex items-start justify-between gap-3"><dt className="font-semibold text-slate-400">Assigned</dt><dd className="truncate text-right font-bold text-slate-700">{selected.assignedAdminName || "Unassigned"}</dd></div>
                    {Number(selected.reopenCount || 0) > 0 ? <div className="flex items-start justify-between gap-3"><dt className="font-semibold text-slate-400">Reopened</dt><dd className="text-right font-black text-violet-600">{selected.reopenCount} time{Number(selected.reopenCount) === 1 ? "" : "s"}</dd></div> : null}
                  </dl>
                </div>

                <div className="mt-3 rounded-[20px] border border-white bg-white p-4 shadow-[0_9px_22px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/70">
                  <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-[10px] bg-amber-50 text-amber-600"><ShoppingBag size={14} /></span><span className="text-[12px] font-black text-slate-800">Linked order</span></div>
                  {detail?.order ? (
                    <div className="mt-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="truncate text-[13px] font-black text-slate-900">{detail.order.orderNumber}</div>
                        <div className="text-[13px] font-black text-slate-900">{money(detail.order.grandTotal, detail.order.currency)}</div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1.5 text-[10px] font-black text-slate-600">{detail.order.status}</span>
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1.5 text-[10px] font-black text-emerald-700">{detail.order.paymentStatus}</span>
                      </div>
                      <div className="mt-3 truncate rounded-[11px] bg-slate-50 px-3 py-2.5 text-[11px] font-semibold text-slate-600">{detail.order.warehouseName || "Warehouse not available"}</div>
                    </div>
                  ) : (
                    <div className="mt-3 rounded-[12px] bg-slate-50 px-3 py-3 text-[11px] font-semibold text-slate-500">No linked order</div>
                  )}
                </div>

                <div className="mt-3 rounded-[20px] border border-white bg-white p-4 shadow-[0_9px_22px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/70">
                  <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-[10px] bg-sky-50 text-sky-600"><Clock3 size={14} /></span><span className="text-[12px] font-black text-slate-800">Activity</span></div>
                  <div className="mt-3 space-y-3 text-[11px]">
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-400">Last message</span><span className="font-black text-slate-700">{relativeTime(selected.lastMessageAt)}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-400">Unread</span><span className="font-black text-[#d65a6d]">{selected.adminUnreadCount || 0}</span></div>
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-400">Priority</span><span className="font-black text-slate-700">{selected.priority || "NORMAL"}</span></div>
                    {selected.reopenedAt ? <div className="flex items-center justify-between"><span className="font-semibold text-slate-400">Last reopened</span><span className="font-black text-violet-600">{relativeTime(selected.reopenedAt)}</span></div> : null}
                    {selected.userClearedAt ? (
                      <>
                        <div className="my-1 h-px bg-slate-100" />
                        <div className="flex items-center justify-between"><span className="font-semibold text-slate-400">Customer view</span><span className="font-black text-sky-600">Cleared</span></div>
                        <div className="flex items-start justify-between gap-3"><span className="font-semibold text-slate-400">Cleared by</span><span className="truncate text-right font-black text-slate-700">{selected.userClearedByName || selected.userClearedBy || "Admin"}</span></div>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      <ConfirmModal open={clearOneOpen} title="Clear chat from customer?" description="This removes the conversation from the customer's support history. Admin history stays available under Cleared history." confirmText="Clear from customer" danger loading={actionLoading} onCancel={() => setClearOneOpen(false)} onConfirm={doClearOne} />
      {canClearAll ? <ConfirmModal open={clearAllOpen} title="Clear all customer chats?" description="All active conversations will be removed from customer support history. Admin history will remain available under Cleared history." confirmText="Clear from customers" danger loading={actionLoading} requiredText="CLEAR ALL CHATS" inputValue={clearAllText} onInput={setClearAllText} onCancel={() => { setClearAllOpen(false); setClearAllText(""); }} onConfirm={doClearAll} /> : null}
    </div>
  );
}
